import React, { useEffect, useState } from 'react';
import Modal from '../../../../../shared/design-system/components/atoms/modal';
import { BULK_SEARCH_FILTERS } from './config';
import Checkbox from '../../../../../shared/design-system/components/atoms/checkbox';
import { BulkFilters } from './type';
import MultiChipInput from '../../../../../shared/components/multi-chip-input';
import {
  getIsErrorFromErrorMapping,
  getMappedValueFromKey,
} from './helper/bulk-search-helper';

type IProps = {
  show: boolean;
  onClose: () => void;
  onSearch: (bulkFilters: BulkFilters) => void;
  focusKey?: string;
  pendingFilters: URLSearchParams;
};

const LeadsFilterBulkSearch: React.FC<IProps> = ({
  show,
  onClose,
  onSearch,
  focusKey,
  pendingFilters,
}) => {
  const [bulkFilters, setBulkFilters] = useState<BulkFilters>({});
  const [bulkFilterError, setBulkFilterError] = useState<
    Record<string, string>
  >({});

  const isError = getIsErrorFromErrorMapping(bulkFilterError);

  const updateBulkFiltersFromPendingFilters = () => {
    const updatedBulkFilters: BulkFilters = {};
    const filtersToShow = focusKey
      ? BULK_SEARCH_FILTERS.filter((f) => f.key === focusKey)
      : BULK_SEARCH_FILTERS;

    filtersToShow.forEach(({ key, checkboxDataList }) => {
      let queryValues = pendingFilters.getAll(key);
      const showExactMatch = pendingFilters.get(`${key}_exactMatch`) === '1';

      queryValues = queryValues.map((value) => value.replace(/"/g, ''));

      if (checkboxDataList) {
        // Considering values with checkboxDataList won't have show exact match
        queryValues = queryValues.map(
          (value) => getMappedValueFromKey(checkboxDataList, value) ?? value,
        );
      }

      updatedBulkFilters[key] = {
        values: queryValues,
        showExactMatch,
      };
    });
    setBulkFilters(updatedBulkFilters);
  };

  useEffect(() => {
    if (show) {
      updateBulkFiltersFromPendingFilters();
    }
  }, [show, pendingFilters, focusKey]);

  const onCheckExactMatch = (key: string, checked: boolean) => {
    setBulkFilters({
      ...bulkFilters,
      [key]: {
        ...(bulkFilters[key] ? bulkFilters[key] : { values: [] }),
        showExactMatch: checked,
      },
    });
  };
  const onValueChange = (key: string, value: string[]) => {
    setBulkFilters({
      ...bulkFilters,
      [key]: {
        ...(bulkFilters[key] ? bulkFilters[key] : { showExactMatch: false }),
        values: value,
      },
    });
  };

  const onSubmit = () => {
    onSearch(bulkFilters);
  };

  const filtersToRender = focusKey
    ? BULK_SEARCH_FILTERS.filter((f) => f.key === focusKey)
    : BULK_SEARCH_FILTERS;

  return (
    <Modal
      show={show}
      titleContent="Bulk Search"
      showCloseIcon={true}
      submitButtonText="Search"
      onHide={onClose}
      onClose={onClose}
      onSubmit={onSubmit}
      isSubmitDisabled={isError}
      className="v2-leads-filter-bulk-search"
    >
      {filtersToRender.map(
        ({
          key,
          icon,
          labelText,
          placeholder,
          showExactMatchCheckbox,
          maxInputCount,
        }) => (
          <div key={key}>
            <div className="search-input-label-container">
              <div className="search-input-label">
                <div className="label-icon">{icon}</div>
                <div className="label">{labelText}</div>
              </div>
              {showExactMatchCheckbox ? (
                <Checkbox
                  value="filterKey"
                  className="show-exact-match-check"
                  onChange={(checked) => onCheckExactMatch(key, checked)}
                  checked={bulkFilters[key]?.showExactMatch}
                  label={
                    <div className="label-container d-inline-block">
                      <p className="checkbox-label-text m-0">
                        Show Exact Match
                      </p>
                    </div>
                  }
                />
              ) : null}
            </div>
            <MultiChipInput
              values={bulkFilters[key]?.values ?? []}
              onChange={(value) => onValueChange(key, value)}
              placeholder={placeholder}
              showInputCount={true}
              maxInputCount={maxInputCount}
              error={bulkFilterError?.[key]}
              onError={(error) => {
                setBulkFilterError((prevError) => ({
                  ...prevError,
                  [key]: error,
                }));
              }}
              scrollTo={focusKey === key}
            />
          </div>
        ),
      )}
    </Modal>
  );
};

export default LeadsFilterBulkSearch;
